// @flow
import React, { Component, createRef, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Messages from './Messages/Messages';
import { OrderedMap } from 'immutable';
import { uploadImageApi } from '../../api';
import autosize from 'autosize';
// import ReportModalContainer from '../../../report/container/ReportModalContainer';
import Modal from '../../../../elements/Modal/Modal';

import { socketConnect } from '../../../../utils/socket';
import style from './Messaging.scss';
import avatarStyle from '../../../../assets/styles/avatar.scss';
import icons from '../../../../assets/styles/icons.scss';
import defaultPic from '../../../../assets/img/profileplaceholder.jpg';
import blueShield from '../../../../assets/img/order/sale/messaging/blue-shield.png';
import orangeShield from '../../../../assets/img/order/sale/messaging/orange-shield.png';

import type { OrderMessage, UserMini, User, Order } from '../../../../entities';
import type { SocketConnection } from '../../../../utils/socket';
import type { OrderType } from '../../duck';
import type { UploadImageResp } from '../../api';

type Props = {
  +orderMessages: OrderMessage[];
  +userId: string;
  +user: User;
  +orderId: string;
  +orderType: OrderType;
  +color?: 'blue' | 'orange';
  +addMessage: (message: OrderMessage) => void;
  +queueMessage: (queuedMessage: OrderMessage) => void;
  +unQueueMessage: (queuedMessageKey: number) => void;
  +postMessage: (orderId: string, message: OrderMessage) => void;
  +receiver: UserMini;
  +order: Order;
};

type State = {
  text: string;
  // textAreaHeight: number;
  // editorState: EditorState;
  photoModalVisible: boolean;
  image: string;
};

const messages = defineMessages({
  inputPlaceholder: {
    id: 'order.messaging.input.placeholder',
  },
});

/*const linkifyPlugin = createLinkifyPlugin({
  style: {
    color: '#11AEFA',
  },
});

const draftToHtmlOptions = {
  entityStyleFn: (entity) => {
    const entityType = entity.get('type').toLowerCase();
    if (entityType === 'link') {
      const data = entity.getData();
      return {
        element: 'a',
        attributes: {
          href: data.url,
          target: '_blank',
        },
      };
    }
  },
};*/

const validFormats = ['image/jpeg', 'image/jpg', 'image/png'];

export default class Messaging extends Component<Props, State> {
  socket: SocketConnection;
  socketAction: string = 'new';
  textRef: HTMLTextAreaElement;
  state = {
    text: '',
    photoModalVisible: false,
    image: '',
  };
  queuedMessages: OrderedMap<number, OrderMessage> = OrderedMap();

  componentDidMount() {
    const { orderId, addMessage, unQueueMessage, userId } = this.props;
    this.socket = socketConnect('messages', {
      order_id: orderId,
    });
    this.socket.on(this.socketAction, (message: OrderMessage) => {
      if (message.transmitterId === userId) {
        const queueMessage = this.queuedMessages.first();
        const queuedMessageKey = queueMessage ? queueMessage.createdAt : 0;
        unQueueMessage(queuedMessageKey);
        this.queuedMessages = this.queuedMessages.delete(queuedMessageKey);
      } else {
        addMessage(message);
      }
    });
    autosize(this.textRef);
  }

  componentWillUnmount() {
    if (this.socket) {
      this.socket.off(this.socketAction);
      this.socket.close();
    }
  }

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({
        text: value,
      });
    }
  };

  _htmlify = (inputText: string) => {
    // Linkify
    let replacedText, replacePattern1, replacePattern2, replacePattern3;

    //URLs starting with http://, https://, or ftp://
    replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
    replacedText = inputText.replace(replacePattern1, '<a href="$1" target="_blank">$1</a>');

    //URLs starting with "www." (without // before it, or it'd re-link the ones done above).
    replacePattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
    replacedText = replacedText.replace(replacePattern2, '$1<a href="http://$2" target="_blank">$2</a>');

    //Change email addresses to mailto:: links.
    replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
    replacedText = replacedText.replace(replacePattern3, '<a href="mailto:$1">$1</a>');

    // Trim extra line breaks at start and end
    replacedText = replacedText.replace(/^\s+|\s+$/g, '');

    // Line break
    replacedText = replacedText.replace(/\n/g, '<br />');

    return `<p>${replacedText}</p>`;
  };

  onSend(e: Event) {
    const { postMessage, orderId, receiver, userId, queueMessage } = this.props;
    const { text } = this.state;
    const html: string = this._htmlify(this.textRef.innerHTML);
    if (text.length > 0 && this.textRef.innerHTML && this.textRef.innerHTML.length > 0) {
      const message: OrderMessage = {
        type: 'transaction-message',
        read: false,
        createdAt: Date.now(),
        message: html,
        //image: '',
        transmitterId: userId,
        receiverId: receiver,
        loading: true,
      };
      postMessage(orderId, message);
      this.queuedMessages = this.queuedMessages.set(message.createdAt, message);
      queueMessage(message);
      this.setState({
        text: '',
      }, () => {
        this.textRef.style.height = '24px';
      });
    }
  }

  onImageUpload(e: SyntheticInputEvent<HTMLInputElement>) {
    const acceptedFiles: FileList = e.target.files;
    const { postMessage, orderId, receiver, userId, queueMessage } = this.props;
    if (acceptedFiles.length && validFormats.includes(acceptedFiles[0].type)) {
      const message: OrderMessage = {
        createdAt: Date.now(),
        receiverId: receiver.id,
        transmitterId: userId,
        loading: true,
      };
      // upload
      const fd = new FormData();
      fd.append('file', acceptedFiles[0]);
      fd.append('resourceGroup', 'MESSAGING');
      uploadImageApi(fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).toPromise().then((uploadImageResponse: UploadImageResp) => {
          postMessage(orderId, { ...message, image: uploadImageResponse.id })
        }
      );
      // preview image
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        const queuedMessage = { ...message, image: fileAsDataURL.toString(), };
        this.queuedMessages = this.queuedMessages.set(message.createdAt, queuedMessage);
        queueMessage(queuedMessage);
      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.readAsDataURL(acceptedFiles[0]);

    }
  }

  handleOpenPhotoModal(image: string) {
    this.setState({ photoModalVisible: true, image })
  }

  handleClosePhotoModal() {
    this.setState({ photoModalVisible: false })
  }

  render() {
    const { color = 'blue', orderMessages, userId, receiver, order, user, orderType } = this.props;
    // const { editorState, photoModalVisible, image } = this.state;
    const { photoModalVisible, image, text } = this.state;
    return (
      <Fragment>
        <section className={style.messagingWrap}>
          <section className={style.messagingContent}>
            <FormattedMessage id="order.messaging.title">{
              txt => <h2><i className={icons.symbolChat}/>{txt}</h2>
            }</FormattedMessage>
            <FormattedMessage id='order.messaging.subtitle'>{
              txt => <p className={style.subtitle}>{txt}</p>
            }</FormattedMessage>
            <div className={style.messagingContentWrap}>
              <article className={style.userWrap}>
                <div className={style.avatarWrap}>
                  <img className={classNames(avatarStyle.avatar, avatarStyle.small)}
                       src={receiver && receiver.image || defaultPic}/>
                  {/*<p className={classNames(style.name, { [style.orange]: color === 'orange' })}>{receiver.firstName}</p>*/}
                  <p className={classNames(style.name)}>{receiver && receiver.firstName}</p>
                </div>
                <FormattedMessage id={color === 'orange' ? 'order.messaging.traveler' : 'order.messaging.purchaser'}>{
                  txt => <p className={style.receiver}>{txt}</p>
                }</FormattedMessage>
                {/*<FormattedMessage id="modal.report.trigger.label">{
                  txt => (
                    <p className={style.reportLabel} onClick={this.handleOpenPhotoModal.bind(this)}>{txt}</p>)
                }</FormattedMessage>*/}
              </article>
              {/*<div className={style.border}/>*/}
              <article className={style.chatWrap}>
                {order && <Messages messages={orderMessages}
                                    userId={userId}
                                    user={user}
                                    color={color}
                                    traveler={order.traveler}
                                    purchaser={order.purchaser}
                                    handleOpenPhoto={this.handleOpenPhotoModal.bind(this)}/>}
                {/*<div className={style.border}/>*/}
                <section className={classNames(style.securityInfo, style[orderType])}>
                  {orderType === 'sale' ? (
                    <img src={orangeShield} alt="Qempo"/>) : (
                    <img src={blueShield} alt="Qempo"/>)}
                  <FormattedMessage id={orderType === 'sale' ? 'order.messaging.securityInfo.traveler' :
                    'order.messaging.securityInfo.purchaser'}>{
                    text => <p className={style[orderType]}>{text}</p>
                  }</FormattedMessage>
                </section>
                <div className={style.send}>
                  <label className={style.buttonImage}>
                    <i className={classNames(icons.symbolCamera, style.cameraIcon)}/>
                    <input type="file"
                           name="image-file"
                           accept="image/jpeg,image/jpg,image/png"
                           className={style.image}
                           onChange={this.onImageUpload.bind(this)}/>
                  </label>
                  {/*<div className={style.editable}
                     contentEditable={true}
                     onInput={this.handleChange.bind(this)}>{text}</div>*/}
                  <div className={style.editableWrap}>
                    <textarea ref={(c: any) => (this.textRef = c)}
                              onChange={this.handleChange}
                              value={text}
                              placeholder={this.context.intl.formatMessage(messages.inputPlaceholder)}
                              rows={1}/>
                    {/*<textarea onChange={this.handleChange}
                              onKeyUp={this.handleKeyUp}
                              ref={this.textRef}
                              value={text}/>*/}
                    {/*<Editor editorState={editorState}
                            onChange={(this.onChange.bind(this): any)}
                            plugins={[linkifyPlugin]}
                            stripPastedStyles={true}
                            placeholder={this.context.intl.formatMessage(messages.inputPlaceholder)}
                            ref={(refEditor: any) => this.refEditor = refEditor}/>*/}
                  </div>
                  <button type="button" onClick={this.onSend.bind(this)}>
                    <i className={classNames(icons.symbolSend, style.sendIcon)}/>
                  </button>
                </div>
              </article>
            </div>
          </section>
        </section>
        <Modal open={photoModalVisible}
               handleClose={this.handleClosePhotoModal.bind(this)}
               singleElementMode
               closeOnEsc>
          <img className={style.propertiesWrap}
               src={image}
               alt='image'/>
        </Modal>
      </Fragment>);
  }
}

Messaging.propTypes = {
  orderMessages: PropTypes.array.isRequired,
  orderId: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  color: PropTypes.string,
  addMessage: PropTypes.func.isRequired,
  queueMessage: PropTypes.func.isRequired,
  unQueueMessage: PropTypes.func.isRequired,
  postMessage: PropTypes.func.isRequired,
  receiver: PropTypes.object,
  order: PropTypes.object,
  user: PropTypes.object,
};

Messaging.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/order/components/Messaging/Messaging.jsx